import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listEmailTemplates,
  saveEmailTemplate,
  type EmailTemplateRow,
} from "@/lib/admin.functions";
import {
  DEFAULT_OPENED_COPY,
  DOWNLOAD_OPENED_FLOW,
  type DownloadLang,
} from "@/lib/emails/download-emails";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

export const Route = createFileRoute("/_authenticated/admin/templates")({
  head: () => ({
    meta: [
      { title: "E-mailtemplates | Beheer IT Solutions" },
      { name: "description", content: "Beheer de teksten van de automatische opvolgflow." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "E-mailtemplates | Beheer IT Solutions" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TemplatesPage,
});

const FLOWS: { flow: string; title: string; help: string }[] = [
  {
    flow: DOWNLOAD_OPENED_FLOW,
    title: "Opvolgmail na openen downloadlink",
    help: "Vertrekt automatisch ±2 uur nadat de aanvrager de downloadlink voor het eerst opent. Placeholders: {{first_name}}, {{last_name}}, {{company}}, {{download_url}}.",
  },
];

const LANGS: DownloadLang[] = ["nl", "fr", "en"];

function TemplatesPage() {
  const listFn = useServerFn(listEmailTemplates);
  const q = useQuery({ queryKey: ["email-templates"], queryFn: () => listFn({}) });

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10 md:px-8">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/admin/leads">{/* i18n-ignore */}← Terug naar leads</Link>
      </Button>

      <div className="mb-8 mt-4">
        <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">E-mailtemplates</h1>
        <p className="text-sm text-muted-foreground">
          Pas de teksten van de automatische vervolgflow aan per taal. Laat je een veld leeg en
          bewaar je, dan valt die taal terug op de standaardtekst.
        </p>
      </div>

      {q.isLoading ? <p className="text-muted-foreground">Laden…</p> : null}
      {q.isError ? (
        <p className="text-destructive">
          {/* i18n-ignore */}Kon de templates niet laden (geen beheerder?).
        </p>
      ) : null}

      {FLOWS.map((f) => (
        <section key={f.flow} className="mb-12">
          <h2 className="text-lg font-medium">{f.title}</h2>
          <p className="mb-4 text-sm text-muted-foreground">{f.help}</p>
          <div className="space-y-6">
            {LANGS.map((lang) => (
              <TemplateEditor
                key={`${f.flow}-${lang}`}
                flow={f.flow}
                lang={lang}
                saved={q.data?.templates.find((t) => t.flow === f.flow && t.lang === lang)}
              />
            ))}
          </div>
        </section>
      ))}
    </main>
  );
}

function TemplateEditor({
  flow,
  lang,
  saved,
}: {
  flow: string;
  lang: DownloadLang;
  saved?: EmailTemplateRow;
}) {
  const qc = useQueryClient();
  const saveFn = useServerFn(saveEmailTemplate);
  const [subject, setSubject] = useState(saved?.subject ?? "");
  const [text, setText] = useState(saved?.text ?? "");

  // Laad opgeslagen waarden zodra de query binnen is.
  useEffect(() => {
    setSubject(saved?.subject ?? "");
    setText(saved?.text ?? "");
  }, [saved?.subject, saved?.text]);

  const defaults = DEFAULT_OPENED_COPY[lang];
  const m = useMutation({
    mutationFn: () => saveFn({ data: { flow, lang, subject, text } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-templates"] }),
  });

  const dirty = subject !== (saved?.subject ?? "") || text !== (saved?.text ?? "");

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{lang.toUpperCase()}</Badge>
          {saved ? (
            <span className="text-xs text-muted-foreground">
              aangepast ·{" "}
              {new Date(saved.updated_at).toLocaleString("nl-BE", {
                dateStyle: "short",
                timeStyle: "short",
              })}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">standaardtekst actief</span>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => m.mutate()}
          disabled={m.isPending || (!dirty && Boolean(saved))}
        >
          {m.isPending ? "Bewaren…" : saved ? "Bewaar wijzigingen" : "Overschrijf standaard"}
        </Button>
      </div>

      <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
        Onderwerp
      </label>
      <Input
        value={subject}
        placeholder={defaults.subject}
        onChange={(e) => setSubject(e.target.value)}
        className="mb-3"
      />

      <label className="mb-1 block text-xs uppercase tracking-wide text-muted-foreground">
        Tekst (lege regel = nieuwe alinea)
      </label>
      <Textarea
        value={text}
        placeholder={defaults.body}
        onChange={(e) => setText(e.target.value)}
        rows={8}
        className="font-mono text-sm"
      />

      {m.isError ? (
        <p className="mt-2 text-sm text-destructive">
          {/* i18n-ignore */}Bewaren mislukt. Probeer opnieuw.
        </p>
      ) : null}
      {m.data?.ok ? (
        <p className="mt-2 text-sm text-emerald-600">
          {m.data.reset ? "Teruggezet naar de standaardtekst." : "Template bewaard."}
        </p>
      ) : null}
    </div>
  );
}
